// Projects (Block J, UI-22: "Projects follows the Team board"). The page, the project view and its
// tabs, on the v2 kit. The presentational pieces are in Projects/ProjectViews.jsx; this file owns
// the data, the filters and every request. Also embedded in the Team dashboard's Projects tab
// (embedded: no page header of its own there).
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { describeWriteFailure } from '../utils/writeFailure';
import { useCurrentUser } from '../contexts/CurrentUserContext';
import { personalFlag, isViewAvailable } from '../nav/views';
import useAutoTour from '../tours/useAutoTour';
import useChatIntro from '../tours/useChatIntro';
import { useWorkspaceRoster, useWorkspaceRosterMap } from '../hooks/useWorkspaceRoster';
import projectManager from '../models/projects';
import CreateTaskModal from './modals/CreateTaskModal';
import CreateProjectModal from './ProjectManagement/CreateProjectModal';
import CreateSprintModal from './ProjectManagement/CreateSprintModal';
import TaskDetailPanel from './ProjectManagement/TaskDetailPanel';
import ProjectFinancials from './Projects/ProjectFinancials';
import DocumentImporter from './Projects/DocumentImporter';
import ProjectTimeTab from './TimeTracking/ProjectTimeTab';
import WorkflowEditor from './WorkflowEditor';
import { api } from '../api/client';
import { messageFromError } from '../utils/apiError';
import { showErrorToast, showToast } from '../utils/toast';
import { formatDate } from '../utils/formatting';
import {
  Banner, Button, ConfirmDialog, EmptyState, ErrorState, Field, Input, List, ListRow, MoveToSheet, PageHeader, PageLayout,
  ProgressBar, RowPrimary, SearchInput, SegmentedControl, Select, Skeleton, StatusPill, TabGroup, TabPanel, Textarea,
  useBreakpoint, useDelayedDelete, AddTaskButton, Reveal,
} from '../ui';
import {
  KanbanView, KpiTiles, OverviewTab, PriorityView, ProjectCard, ProjectDot, ProjectStatusPill, TaskListView,
  boardColumns, columnOf, isInternal, isProjectOverdue, isTaskOutstanding, isTaskOverdue, minutesOf, plural, dayText,
} from './Projects/ProjectViews';
import { priorityKey } from './Team/TaskViews';
import './Projects/projects.css';
import LinkedNotes from './Notes/LinkedNotes';   // decision 90 v2
import ProjectEmails from './Projects/ProjectEmails';   // Email Phase 1

const useTz = () => useCurrentUser()?.currentUser?.timezone;
const hoursOf = (tasks) => Math.round((tasks.reduce((s, t) => s + minutesOf(t), 0) / 60) * 10) / 10;

// The project's tabs, each in its own pastel (brief "Tabs").
const PROJECT_TABS = [
  { id: 'overview', label: 'Overview', color: 'pastel-blue' },
  { id: 'workflow', label: 'Workflow', color: 'pastel-periwinkle' },
  { id: 'tasks', label: 'Tasks', color: 'pastel-yellow' },
  { id: 'files', label: 'Files', color: 'pastel-mint' },
  { id: 'time', label: 'Time', color: 'pastel-peach' },
  { id: 'finances', label: 'Finances', color: 'pastel-green' },
  { id: 'notes', label: 'Notes', color: 'pastel-pink' },
];
const TASK_VIEWS = [
  { value: 'list', label: 'List' },
  { value: 'kanban', label: 'Board' },
  { value: 'priority', label: 'Priority' },
];

// ── Main component ────────────────────────────────────────────────────────────

export default function ProjectTracker({ onCreateProjectInvoice, onNavigate, embedded = false }) {
  const { currentUser, activeWorkspaceId, workspaces } = useCurrentUser();
  const isPersonal = personalFlag(workspaces, activeWorkspaceId);
  // Timesheet is a Business page: in Personal the Hours logged tile is not a link.
  const timesheetAvailable = isViewAvailable('Timesheet', isPersonal);
  const tz = useTz();
  const phone = useBreakpoint() === 'phone';
  const rosterMap = useWorkspaceRosterMap();
  const { members: allUsers } = useWorkspaceRoster();

  // Data
  const [projects, setProjects]   = useState([]);
  const [allTasks, setAllTasks]   = useState([]);
  const [tasks, setTasks]         = useState([]);
  const [, setSprints]            = useState([]);   // a project's sprints (read by the sprint form from the model)
  const [sections, setSections]   = useState([]);
  const [loading, setLoading]     = useState(true);

  // Selection
  const [selectedProject, setSelectedProject] = useState(null);
  const [selectedSprint, setSelectedSprint]   = useState(null);

  // Landing view: 'grid' | 'list'
  const [landingView, setLandingView] = useState(() => localStorage.getItem('project-landing-view') || 'grid');

  // Project tabs: 'overview' | 'workflow' | 'tasks' | 'files' | 'time' | 'finances' | 'notes'
  const [activeTab, setActiveTab] = useState('tasks');

  // Workflow stages/columns for the active project
  const [projectWorkflow, setProjectWorkflow] = useState({ stages: [], columns: [] });

  // Task view inside Tasks tab: 'list' | 'kanban' | 'priority'
  const [taskView, setTaskView] = useState(() => localStorage.getItem('project-task-view') || 'list');

  // Modals
  const [showCreateProject, setShowCreateProject] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [editProject, setEditProject]             = useState(null);
  const [projectSaveFailure, setProjectSaveFailure] = useState('');
  const [showCreateSprint, setShowCreateSprint]   = useState(false);
  const [editSprint, setEditSprint]               = useState(null);
  const [showCreateTask, setShowCreateTask]       = useState(false);
  const [showImporter, setShowImporter]           = useState(false);

  // Detail panel
  const [openTaskId, setOpenTaskId] = useState(null);

  // Drag and drop, and its tap route (Move to…)
  const [draggingId, setDraggingId] = useState(null);
  const [moving, setMoving] = useState(null);

  // Task filters
  const [searchText, setSearchText]         = useState('');
  const [filterPriority, setFilterPriority] = useState('');
  const [filterAssignee, setFilterAssignee] = useState('');

  // Open project browser tabs (UI only, reset on reload)
  const [openProjectTabs, setOpenProjectTabs] = useState([]);

  // Block I: a failed load is said as one. It used to be swallowed, and the landing then said
  // "No projects yet" (and a project "No tasks yet") over data that had simply not arrived.
  const [loadError, setLoadError] = useState('');

  // Section management
  const [newSectionName, setNewSectionName]   = useState('');
  const [showAddSection, setShowAddSection]   = useState(false);

  // ── Data loading ──────────────────────────────────────────────────────────

  const loadData = useCallback(async () => {
    let failure = '';
    try { await projectManager.refreshProjects(); }
    catch (err) { failure = messageFromError(err, 'Your projects could not be loaded.'); }
    const allProjects = projectManager.getAllProjects();

    if (selectedProject) {
      const updated = allProjects.find(p => p.id === selectedProject.id) || null;
      setSelectedProject(updated);
      if (updated) {
        try { await projectManager.refreshProjectSubresources(updated.id); }
        catch (err) { failure = failure || messageFromError(err, "This project's tasks could not be loaded."); }
        setTasks(projectManager.getTasksByProject(updated.id));
        setSprints(projectManager.getSprintsByProject(updated.id));
        setSections(projectManager.getSectionsByProject(updated.id));
      }
    } else if (!failure) {
      // Block J: the landing's tiles and every card's progress need every project's tasks, not only
      // those of projects already opened (it counted nothing on a fresh visit).
      try { await projectManager.refreshAllProjectTasks(); }
      catch (err) { failure = messageFromError(err, 'Your projects\' tasks could not be loaded.'); }
      setTasks(projectManager.getTasksByUser(currentUser?.id));
      setSprints([]);
      setSections([]);
    }
    projectManager.updateAllTaskPriorities();
    setProjects([...projectManager.getAllProjects()]);
    setAllTasks([...projectManager.getAllTasks()]);
    if (selectedProject) setTasks(projectManager.getTasksByProject(selectedProject.id));
    setLoadError(failure);
    setLoading(false);
  }, [selectedProject, currentUser]);

  useEffect(() => {
    setSelectedProject(null);
    setOpenProjectTabs([]);
    setLoading(true);
    loadData();
  }, [activeWorkspaceId]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleTabSelect = (id) => {
    if (id === 'portfolio') {
      handleBackToLanding();
    } else {
      const p = projectManager.getProject(parseInt(id, 10));
      if (p) handleSelectProject(p);
    }
  };

  const handleSelectProject = async (project) => {
    const fresh = projectManager.getProject(project.id);
    setSelectedProject(fresh);
    setSelectedSprint(null);
    setActiveTab('tasks');
    setSearchText('');
    setFilterPriority('');
    setFilterAssignee('');
    setProjectWorkflow({ stages: [], columns: [] });
    // Add to open tabs if not already present
    setOpenProjectTabs(prev => {
      if (prev.some(t => t.id === project.id)) return prev;
      return [...prev, { id: project.id, name: project.name, type: project.projectType || project.project_type }];
    });
    try { await projectManager.refreshProjectSubresources(project.id); setLoadError(''); }
    catch (err) { setLoadError(messageFromError(err, "This project's tasks could not be loaded.")); }
    setTasks(projectManager.getTasksByProject(project.id));
    setSprints(projectManager.getSprintsByProject(project.id));
    setSections(projectManager.getSectionsByProject(project.id));
    setAllTasks([...projectManager.getAllTasks()]);
    fetchProjectWorkflow(project.id);
  };

  const handleCloseTab = (id) => {
    const numId = parseInt(id, 10);
    setOpenProjectTabs(prev => prev.filter(t => t.id !== numId));
    if (selectedProject?.id === numId) handleBackToLanding();
  };

  const handleBackToLanding = () => {
    setSelectedProject(null);
    setSelectedSprint(null);
    setTasks(projectManager.getTasksByUser(currentUser?.id));
    setSprints([]);
    setSections([]);
    setAllTasks([...projectManager.getAllTasks()]);
  };

  // ── Filtered tasks ────────────────────────────────────────────────────────

  // Block J: member ids are UUID strings; this compared them with parseInt, so filtering by a
  // member hid every task.
  const filteredTasks = useMemo(() => tasks.filter(t => {
    if (searchText && !String(t.title || '').toLowerCase().includes(searchText.toLowerCase())) return false;
    if (filterPriority && priorityKey(t.priority) !== filterPriority.toLowerCase()) return false;
    if (filterAssignee && !(t.assignedTo || []).map(String).includes(String(filterAssignee))) return false;
    return true;
  }), [tasks, searchText, filterPriority, filterAssignee]);
  const filtering = !!(searchText || filterPriority || filterAssignee);

  // ── Handlers ──────────────────────────────────────────────────────────────

  const fetchProjectWorkflow = async (projectId) => {
    try {
      const r = await api(`/api/projects/${projectId}/workflow`);   // api() throws on a non-2xx
      const d = await r.json();
      setProjectWorkflow({ stages: d.stages || [], columns: d.columns || [] });
    } catch (err) {
      // Without its stages the board falls back to the plain columns, so say why.
      showErrorToast(messageFromError(err, "This project's workflow stages could not be loaded."));
    }
  };

  // Block J: throws on failure, so the dialog (which now waits for this) stays open and says why.
  const handleCreateProject = async (data) => {
    try {
      if (editProject) {
        await projectManager.updateProject(editProject.id, data);
      } else {
        const created = await projectManager.createProject(data);
        // Post workflow stages/columns if provided
        const draft = data.workflowDraft || {};
        if (created?.id && (draft.stages?.length || draft.columns?.length)) {
          const pid = created.id;
          for (const stage of (draft.stages || [])) {
            await api(`/api/projects/${pid}/stages`, {
              method: 'POST',
              body: JSON.stringify({
                name: stage.name, colour: stage.colour || '#94a3b8',
                position: stage.position ?? 0,
                isTerminal: stage.isTerminal ?? false,
                isStart: stage.isStart ?? false,
              }),
            });
          }
          for (const col of (draft.columns || [])) {
            await api(`/api/projects/${pid}/columns`, {
              method: 'POST',
              body: JSON.stringify({
                name: col.name, fieldKey: col.fieldKey,
                fieldType: col.fieldType || 'TEXT',
                options: col.options ?? null,
                position: col.position ?? 0,
                isRequired: col.isRequired ?? false,
              }),
            });
          }
        }
      }
      setEditProject(null);
      setProjectSaveFailure('');
      await loadData();
    } catch (err) {
      console.error('Failed to save project:', err);
      const message = describeWriteFailure(err, 'save that project');
      setProjectSaveFailure(message);
      throw new Error(message);
    }
  };

  // Decision 7: the project disappears at once with Undo; the DELETE is sent when the window closes.
  const projectDelete = useDelayedDelete({
    send: (p, { keepalive } = {}) => api(`/api/projects/${p.id}`, { method: 'DELETE', keepalive }),
    onDeleted: (p) => {
      Object.assign(projectManager, {
        projects: projectManager.projects.filter(x => Number(x.id) !== Number(p.id)),
        projectTasks: projectManager.projectTasks.filter(t => Number(t.projectId) !== Number(p.id)),
      });
      setProjects([...projectManager.getAllProjects()]);
      setAllTasks([...projectManager.getAllTasks()]);
      setOpenProjectTabs(prev => prev.filter(t => t.id !== p.id));
    },
    describe: (p) => `"${p.name}" deleted.`,
    failMessage: 'That project could not be deleted.',
  });
  const handleDeleteProject = (project) => {
    if (selectedProject?.id === project.id) handleBackToLanding();
    projectDelete.remove(project);
  };
  const visibleProjects = useMemo(() => projects.filter(p => !projectDelete.isHidden(p.id)), [projects, projectDelete]);

  const handleCreateSprint = async (data) => {
    try {
      if (editSprint) await projectManager.saveSprint(editSprint.id, data);
      else await projectManager.createSprint(data);
    } catch (err) { showErrorToast(describeWriteFailure(err, 'save that sprint')); }
    setEditSprint(null);
    await loadData();
  };

  // Block J: throws, so the new-task dialog stays open with what was typed and the reason.
  const handleCreateTask = async (data) => {
    try { await projectManager.createProjectTask(data); }
    catch (err) { throw new Error(describeWriteFailure(err, 'add that task')); }
    await loadData();
  };

  // Shows the change at once; if the server refuses it the task goes back and the server says why.
  const handleTaskStatusChange = async (taskId, newStatus) => {
    const pending = projectManager.saveProjectTask(taskId, { status: newStatus });
    loadData();
    try { await pending; }
    catch (err) { showErrorToast(messageFromError(err, 'That status change was not saved.')); }
    await loadData();
  };

  const handleAddSection = async () => {
    const name = newSectionName.trim();
    if (!name || !selectedProject) return;
    try {
      await projectManager.createSection(selectedProject.id, name);
      setNewSectionName('');
      setShowAddSection(false);
    } catch (err) {
      // The name stays in the box, so it can be tried again.
      showErrorToast(describeWriteFailure(err, 'add that section'));
    }
    await loadData();
  };

  // One move, for the drag and for "Move to…" alike.
  // Decision 3: a workflow-stage column is a stage of this project (project_tasks.stage_id), so the
  // move is kept on the project task itself. (It was once sent as PUT /api/tasks/:id with the project
  // task's id, which names a task in a different table; Block J then refused it with a message.)
  const moveTask = async (task, col) => {
    if (!task || !col) return;
    const stageMove = !!col.id;
    if (stageMove ? columnOf(task, columns, projectWorkflow.stages)?.key === col.key : task.status === col.key) return;
    const change = stageMove ? { stageId: col.id } : { status: col.key };
    // Block I: a refused move goes back AND says why (it used to revert, or not, in silence).
    try {
      const pending = projectManager.saveProjectTask(task.id, change);
      loadData();
      await pending;
      showToast(`"${task.title}" moved to ${col.label}.`, { type: 'success' });
    } catch (err) {
      showErrorToast(messageFromError(err, 'That move was not saved.'));
    }
    await loadData();
  };

  const handleDragStart = (e, taskId) => {
    setDraggingId(taskId);
    try { e.dataTransfer.effectAllowed = 'move'; } catch { /* some browsers lock it */ }
  };

  const handleDrop = async (e, col) => {
    e.preventDefault();
    if (!draggingId) return;
    const task = tasks.find(t => String(t.id) === String(draggingId));
    setDraggingId(null);
    await moveTask(task, col);
  };

  const setTaskViewPersist = (v) => {
    setTaskView(v);
    try { localStorage.setItem('project-task-view', v); } catch { /* private window */ }
  };

  const setLandingViewPersist = (v) => {
    setLandingView(v);
    try { localStorage.setItem('project-landing-view', v); } catch { /* private window */ }
  };

  const openNewProject = () => { setEditProject(null); setProjectSaveFailure(''); setShowCreateProject(true); };
  const openEditProject = () => { setEditProject(selectedProject); setProjectSaveFailure(''); setShowCreateProject(true); };

  // ── Derived ───────────────────────────────────────────────────────────────

  const columns = useMemo(() => boardColumns(projectWorkflow.stages), [projectWorkflow.stages]);
  const listGroups = useMemo(() => {
    const groups = sections.map(sec => ({ id: sec.id, name: sec.name, tasks: filteredTasks.filter(t => t.sectionId === sec.id) }));
    const unsectioned = filteredTasks.filter(t => !t.sectionId || !sections.find(s => s.id === t.sectionId));
    if (unsectioned.length > 0 || sections.length === 0) groups.push({ id: null, name: sections.length > 0 ? 'No section' : '', tasks: unsectioned });
    return groups;
  }, [filteredTasks, sections]);
  const statsFor = useCallback((projectId) => projectManager.getProjectStats(projectId), [allTasks]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Render ────────────────────────────────────────────────────────────────

  const openTabs = openProjectTabs.length > 0 && (
    <nav className="pj-open" aria-label="Open projects">
      <span className="pj-open__tab" aria-current={!selectedProject ? 'true' : undefined}>
        <button type="button" className="pj-open__go" onClick={() => handleTabSelect('portfolio')}>All projects</button>
      </span>
      {openProjectTabs.filter(t => !projectDelete.isHidden(t.id)).map(t => (
        <span key={t.id} className="pj-open__tab" aria-current={selectedProject?.id === t.id ? 'true' : undefined}>
          <button type="button" className="pj-open__go" title={t.name} onClick={() => handleTabSelect(String(t.id))}>{t.name}</button>
          <button type="button" className="pj-open__close" aria-label={`Close ${t.name}`} onClick={() => handleCloseTab(String(t.id))}>×</button>
        </span>
      ))}
    </nav>
  );

  let body;
  if (loading) {
    body = <Skeleton lines={5} label="Loading your projects" />;
  } else if (loadError) {
    body = <ErrorState title={selectedProject ? 'This project did not load' : 'Your projects did not load'} message={loadError} onRetry={() => loadData()} />;
  } else if (!selectedProject) {
    body = (
      <PortfolioLanding
        projects={visibleProjects}
        allTasks={allTasks}
        statsFor={statsFor}
        landingView={landingView}
        onSetLandingView={setLandingViewPersist}
        onSelectProject={handleSelectProject}
        onNewProject={openNewProject}
        onImportDocument={() => setShowImporter(true)}
        onNavigate={onNavigate}
        timesheetAvailable={timesheetAvailable}
        tz={tz}
        rosterMap={rosterMap}
        embedded={embedded}
      />
    );
  } else {
    const p = selectedProject;
    const internal = isInternal(p);
    body = (
      <div className="pj-stack">
        <header className="pj-head">
          <div><Button size="sm" variant="tertiary" onClick={handleBackToLanding}>← Projects</Button></div>
          <div className="pj-head__title">
            <ProjectDot color={p.color} />
            <h2 className="pj-head__h">{p.name}</h2>
            <ProjectStatusPill status={p.status} />
          </div>
          {(internal ? p.category : p.client) && <p className="pj-muted">{internal ? p.category : p.client}</p>}
          <div className="pj-head__actions">
            <Button variant="secondary" onClick={openEditProject}>Edit</Button>
            {onCreateProjectInvoice && (
              <Button variant="tertiary" onClick={() => onCreateProjectInvoice(p.id)}>£ Create invoice</Button>
            )}
            <Button variant="destructive" onClick={() => setDeleteConfirm({ id: p.id, name: p.name })}>Delete</Button>
          </div>
        </header>

        <TabGroup label={`${p.name} sections`} idPrefix="pj" tabs={PROJECT_TABS} value={activeTab} onChange={setActiveTab} />

        <TabPanel idPrefix="pj" tabId={activeTab}>
          {activeTab === 'overview' && (
            <OverviewTab project={p} stats={statsFor(p.id)} hoursLogged={hoursOf(tasks)} members={allUsers} onEdit={openEditProject} />
          )}

          {activeTab === 'tasks' && (
            <div className="pj-stack pj-stack--tight">
              <div className="pj-toolbar">
                <SearchInput label="Search tasks" placeholder="Search tasks…" value={searchText} onChange={e => setSearchText(e.target.value)} />
                <Select className="pj-toolbar__select" aria-label="Priority" value={filterPriority} onChange={e => setFilterPriority(e.target.value)}
                  options={[{ value: '', label: 'All priorities' }, { value: 'High', label: 'High' }, { value: 'Medium', label: 'Medium' }, { value: 'Low', label: 'Low' }]} />
                <Select className="pj-toolbar__select" aria-label="Member" value={filterAssignee} onChange={e => setFilterAssignee(e.target.value)}
                  options={[{ value: '', label: 'All members' }, ...allUsers.map(u => ({ value: String(u.id), label: u.name }))]} />
                <div className="pj-toolbar__end">
                  <SegmentedControl label="Show tasks as" options={TASK_VIEWS} value={taskView} onChange={setTaskViewPersist} />
                  <Button variant="tertiary" onClick={() => setActiveTab('workflow')}>Edit workflow</Button>
                  <AddTaskButton onClick={() => setShowCreateTask(true)} />
                </div>
              </div>

              {taskView === 'list' && (
                <TaskListView groups={listGroups} filtered={filtering} tz={tz} rosterMap={rosterMap}
                  onOpen={setOpenTaskId} onStatusChange={handleTaskStatusChange} onNewTask={() => setShowCreateTask(true)}>
                  {showAddSection ? (
                    <div className="pj-add-section">
                      <Input autoFocus aria-label="Section name" value={newSectionName} placeholder="Section name…"
                        onChange={e => setNewSectionName(e.target.value)}
                        onKeyDown={e => { if (e.key === 'Enter') handleAddSection(); if (e.key === 'Escape') setShowAddSection(false); }} />
                      <Button variant="confirm" onClick={handleAddSection} disabled={!newSectionName.trim()}>Add section</Button>
                      <Button variant="tertiary" onClick={() => setShowAddSection(false)}>Cancel</Button>
                    </div>
                  ) : (
                    <div><Button variant="tertiary" onClick={() => setShowAddSection(true)}>+ Add section</Button></div>
                  )}
                </TaskListView>
              )}
              {taskView === 'kanban' && (
                <>
                  {!phone && <p className="pj-muted">Drag a card to another column, or use Move to… on the card.</p>}
                  <KanbanView tasks={filteredTasks} columns={columns} stages={projectWorkflow.stages} tz={tz} rosterMap={rosterMap}
                    draggingId={draggingId} onDragStart={handleDragStart} onDragEnd={() => setDraggingId(null)} onDrop={handleDrop}
                    onOpen={setOpenTaskId} onMove={setMoving} />
                </>
              )}
              {taskView === 'priority' && (
                <PriorityView tasks={filteredTasks} filtered={filtering} tz={tz} rosterMap={rosterMap}
                  onOpen={setOpenTaskId} onStatusChange={handleTaskStatusChange} />
              )}
            </div>
          )}

          {activeTab === 'workflow' && (
            <div className="pj-stack pj-stack--tight">
              <Banner tone="notice">Changes to stages here affect this project only. Tasks on a stage you remove will need moving.</Banner>
              <WorkflowEditor
                mode="project"
                entityId={p.id}
                readOnly={false}
                showColumns={true}
                showAutomations={true}
                showSaveAsTemplate={true}
                onSaved={() => fetchProjectWorkflow(p.id)}
              />
            </div>
          )}

          {activeTab === 'files' && <FilesTab project={p} />}
          {activeTab === 'time' && <ProjectTimeTab project={p} />}
          {activeTab === 'finances' && (
            <ProjectFinancials projectId={p.id} onBack={() => setActiveTab('tasks')} />
          )}
          {activeTab === 'notes' && <NotesTab project={p} />}
        </TabPanel>
      </div>
    );
  }

  const overlays = (
    <>
      {openTaskId && (
        <TaskDetailPanel
          taskId={openTaskId}
          onClose={() => { setOpenTaskId(null); loadData(); }}
          onUpdated={() => loadData()}
        />
      )}

      <CreateProjectModal
        isOpen={showCreateProject}
        onClose={() => { setShowCreateProject(false); setEditProject(null); setProjectSaveFailure(''); }}
        onSave={handleCreateProject}
        editProject={editProject}
        saveFailure={projectSaveFailure}
      />

      <CreateSprintModal
        isOpen={showCreateSprint}
        onClose={() => { setShowCreateSprint(false); setEditSprint(null); }}
        onSave={handleCreateSprint}
        projectId={selectedProject?.id}
        editSprint={editSprint}
      />

      <CreateTaskModal
        isOpen={showCreateTask}
        onClose={() => setShowCreateTask(false)}
        onSave={handleCreateTask}
        projectId={selectedProject?.id}
        sprintId={selectedSprint?.id}
      />

      {showImporter && (
        <DocumentImporter
          onClose={() => setShowImporter(false)}
          onViewProject={(projectId) => {
            setShowImporter(false);
            projectManager.refreshProjects().then(() => {
              const found = projectManager.getAllProjects().find(proj => proj.id === projectId);
              if (found) handleSelectProject(found);
            }).catch((err) => showErrorToast(messageFromError(err, 'The imported project could not be opened. Refresh to see it.')));
          }}
        />
      )}

      <ConfirmDialog
        open={deleteConfirm !== null}
        onClose={() => setDeleteConfirm(null)}
        onConfirm={() => { const p = projects.find(x => x.id === deleteConfirm?.id); if (p) handleDeleteProject(p); }}
        itemType="project"
        itemName={deleteConfirm?.name || ''}
        warningText={`This deletes "${deleteConfirm?.name || 'this project'}" and all its tasks. You can undo it for a few seconds afterwards.`}
      />

      <MoveToSheet
        open={!!moving}
        onClose={() => setMoving(null)}
        itemName={moving?.title}
        current={moving ? columnOf(moving, columns, projectWorkflow.stages)?.key : undefined}
        destinations={columns.map(c => ({ id: c.key, label: c.label }))}
        onMove={(d) => { const t = moving; const col = columns.find(c => c.key === d.id); if (t && col) moveTask(t, col); }}
      />
    </>
  );

  if (embedded) {
    return (
      <div className="project-tracker-root pj-stack">
        {openTabs}
        {body}
        {overlays}
      </div>
    );
  }

  // Decision 88, Projects exception (Carla, 2026-10-21): the two header actions are BUTTONS here,
  // "+ New project" the green create button and "Import from document" a secondary button.
  const primary = !selectedProject
    ? (
      <>
        <Button variant="create" className="ui-addtask" data-green-create data-tour-id="projects-new-project" onClick={openNewProject}>+ New project</Button>
        <Button variant="secondary" data-header-button onClick={() => setShowImporter(true)}>Import from document</Button>
      </>
    )
    : null;
  const subline = selectedProject
    ? `${plural(tasks.length, 'task')} · ${tasks.filter(isTaskOutstanding).length} outstanding`
    : `${plural(visibleProjects.length, 'project')} in your workspace`;
  return (
    <PageLayout>
      <PageHeader
        eyebrow={{ label: isPersonal ? 'My stuff' : 'My work', color: 'group-my-work' }}
        title="Projects"
        help="projects"
        subline={loading || loadError ? undefined : subline}
        primary={primary}
      />
      <div className="pj-stack">
        {openTabs}
        {body}
      </div>
      {overlays}
    </PageLayout>
  );
}

// ── Portfolio Landing ─────────────────────────────────────────────────────────

const LIST_COLUMNS = [
  { key: 'name', label: 'Project', role: 'primary', width: 'minmax(0, 2fr)', sortable: true },
  { key: 'client', label: 'Client or category', width: 'minmax(0, 1.2fr)' },
  { key: 'status', label: 'Status', role: 'status', width: '120px', sortable: true },
  { key: 'progress', label: 'Progress', width: 'minmax(120px, 1fr)' },
  { key: 'deadline', label: 'Due', width: '110px', sortable: true },
];
const SORT_TO_KEY = { name: 'name', deadline: 'deadline', status: 'status' };

// Which tab a project shows under (unchanged): internal, or an untyped project with no client.
function onTab(p, tab) {
  const type = p.projectType || p.project_type;
  if (tab === 'internal') return type === 'internal' || (!type && !p.client && !p.clientId);
  return type !== 'internal';
}

function PortfolioLanding({ projects, allTasks, statsFor, landingView, onSetLandingView, onSelectProject, onNewProject, onImportDocument, onNavigate, timesheetAvailable = true, tz, rosterMap, embedded }) {
  useAutoTour('projects');
  useChatIntro('projects');
  const [sortBy, setSortBy] = useState('status');
  const [typeFilter, setTypeFilter] = useState('client');
  const [statusFilter, setStatusFilter] = useState(null);
  const listRef = useRef(null);

  // Block J: from the tasks as loaded now (it read a list captured once, before anything loaded).
  const totalStats = useMemo(() => ({
    active: projects.filter(p => p.status === 'active').length,
    outstanding: allTasks.filter(isTaskOutstanding).length,
    overdue: allTasks.filter((t) => isTaskOverdue(t, tz)).length,
    hoursLogged: hoursOf(allTasks),
  }), [projects, allTasks, tz]);

  const counts = useMemo(() => ({
    client: projects.filter(p => onTab(p, 'client')).length,
    internal: projects.filter(p => onTab(p, 'internal')).length,
  }), [projects]);

  const typeFiltered = useMemo(() => projects.filter(p => onTab(p, typeFilter) && (!statusFilter || p.status === statusFilter)),
    [projects, typeFilter, statusFilter]);

  const sorted = useMemo(() => {
    const list = [...typeFiltered];
    if (sortBy === 'name')     return list.sort((a, b) => a.name.localeCompare(b.name));
    if (sortBy === 'deadline') return list.sort((a, b) => (a.deadline || '9999') < (b.deadline || '9999') ? -1 : 1);
    // Default: active first
    return list.sort((a, b) => {
      const order = { active: 0, 'on hold': 1, completed: 2 };
      return (order[a.status] ?? 3) - (order[b.status] ?? 3);
    });
  }, [typeFiltered, sortBy]);

  const tiles = [
    {
      label: 'Active projects', value: totalStats.active, color: 'pastel-mint', pressed: statusFilter === 'active',
      hint: statusFilter === 'active' ? 'Showing active only' : 'Show active only',
      onClick: () => {
        setStatusFilter(f => (f === 'active' ? null : 'active'));
        setTimeout(() => { try { listRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }); } catch { /* older WebView */ } }, 50);
      },
    },
    { label: 'Outstanding tasks', value: totalStats.outstanding, color: 'pastel-yellow' },
    {
      label: 'Overdue tasks', value: totalStats.overdue, color: totalStats.overdue > 0 ? 'pastel-coral' : 'pastel-neutral',
      hint: onNavigate ? 'Open My tasks' : undefined,
      onClick: onNavigate ? () => onNavigate('Tasks') : undefined,
    },
    {
      label: 'Hours logged', value: `${totalStats.hoursLogged}h`, color: 'pastel-periwinkle',
      // Timesheet is a Business page; in a Personal workspace this tile is a figure, not a link.
      hint: timesheetAvailable && onNavigate ? 'Open Timesheet' : undefined,
      onClick: timesheetAvailable && onNavigate ? () => onNavigate('Timesheet') : undefined,
    },
  ];

  const sort = { key: SORT_TO_KEY[sortBy] || 'status', dir: 'asc' };

  return (
    <div className="pj-stack">
      {embedded && (
        <div className="pj-toolbar">
          <h2 className="pj-head__h">Projects</h2>
          <div className="pj-toolbar__end">
            <Button variant="tertiary" onClick={onImportDocument}>Import from document</Button>
            <Button variant="create" data-tour-id="projects-new-project" onClick={onNewProject}>+ New project</Button>
          </div>
        </div>
      )}

      <div data-tour-id="projects-stats"><KpiTiles tiles={tiles} /></div>

      <div className="pj-toolbar">
        <TabGroup label="Project type" idPrefix="pj-type" value={typeFilter} onChange={setTypeFilter}
          tabs={[
            { id: 'client', label: 'Client projects', color: 'pastel-blue', count: counts.client },
            { id: 'internal', label: 'Internal projects', color: 'pastel-lilac', count: counts.internal },
          ]} />
        <div className="pj-toolbar__end" data-tour-id="projects-view-toggle">
          {landingView === 'list' && !embedded && (
            <Select className="pj-toolbar__select" aria-label="Sort projects" value={sortBy} onChange={e => setSortBy(e.target.value)}
              options={[{ value: 'status', label: 'Sort: Status' }, { value: 'name', label: 'Sort: Name' }, { value: 'deadline', label: 'Sort: Due date' }]} />
          )}
          <SegmentedControl label="Show projects as" value={landingView} onChange={onSetLandingView}
            options={[{ value: 'grid', label: 'Cards' }, { value: 'list', label: 'List' }]} />
        </div>
      </div>

      {statusFilter && (
        <Banner tone="info" action={<Button size="sm" variant="secondary" onClick={() => setStatusFilter(null)}>Show all</Button>}>
          Showing active projects only.
        </Banner>
      )}

      {/* Project list anchor: used for scroll-to on the Active projects tile */}
      <div ref={listRef} />

      {sorted.length === 0 && (
        projects.length === 0
          ? <EmptyState title="No projects yet" action={<Button variant="create" onClick={onNewProject}>+ New project</Button>}>Create your first project to get started.</EmptyState>
          : <EmptyState title={`No ${typeFilter === 'internal' ? 'internal' : 'client'} projects${statusFilter ? ' that are active' : ''}`}
              action={<Button variant="create" onClick={onNewProject}>+ New project</Button>}>
              {statusFilter ? 'Show all projects, or create a new one.' : 'Your other projects are on the other tab.'}
            </EmptyState>
      )}

      {landingView === 'grid' && sorted.length > 0 && (
        <div className="pj-grid" data-tour-id="projects-list">
          {sorted.map(p => (
            <ProjectCard key={p.id} project={p} stats={statsFor(p.id)} tz={tz} rosterMap={rosterMap} onOpen={onSelectProject} />
          ))}
        </div>
      )}

      {landingView === 'list' && sorted.length > 0 && (
        <div data-tour-id="projects-list">
          <List label="Projects" columns={LIST_COLUMNS} sort={sort}
            onSortChange={(s) => setSortBy(s.key === 'name' ? 'name' : s.key === 'deadline' ? 'deadline' : 'status')}>
            {sorted.map(p => {
              const stats = statsFor(p.id);
              const pct = stats.totalTasks > 0 ? Math.round(stats.completedTasks / stats.totalTasks * 100) : 0;
              const overdue = isProjectOverdue(p, tz);
              const internal = isInternal(p);
              const clientLabel = internal ? (p.category || 'Internal') : (p.client || 'No client');
              const due = p.deadline ? `${overdue ? 'Overdue, ' : ''}${dayText(p.deadline)}` : 'No date';
              return (
                <ListRow key={p.id} openLabel={`Open ${p.name}`} onOpen={() => onSelectProject(p)}
                  meta={`${clientLabel} · ${stats.completedTasks} of ${plural(stats.totalTasks, 'task')} done · ${p.deadline ? `Due ${due}` : 'No due date'}`}
                  cells={{
                    name: <RowPrimary leading={<ProjectDot color={p.color} />} name={<span className="pj-name">{p.name}</span>} sub={internal ? 'Internal' : 'Client'} />,
                    client: clientLabel,
                    status: <ProjectStatusPill status={p.status} />,
                    progress: <ProgressBar label={`${p.name} progress`} value={pct} valueText={`${stats.completedTasks} of ${stats.totalTasks}`} />,
                    deadline: overdue ? <StatusPill tone="warning">{due}</StatusPill> : due,
                  }} />
              );
            })}
          </List>
        </div>
      )}
    </div>
  );
}

// ── Files Tab ─────────────────────────────────────────────────────────────────

const SOURCE_LABEL = { google_drive: 'Google Drive', onedrive: 'OneDrive', url: 'Link' };

function ProjFileIcon({ source, mimeType }) {
  let icon = '📎';
  if (source === 'google_drive' || source === 'onedrive' || source === 'url') icon = '🔗';
  else if (mimeType?.startsWith('image/')) icon = '🖼️';
  else if (mimeType === 'application/pdf') icon = '📄';
  return <span className="pj-file__icon" aria-hidden="true">{icon}</span>;
}

function FilesTab({ project }) {
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addingFile, setAddingFile] = useState(false);
  const [newFileName, setNewFileName] = useState('');
  const [newFileUrl, setNewFileUrl] = useState('');
  const [saving, setSaving] = useState(false);
  const [linkingFile, setLinkingFile] = useState(false);
  const [linkSource, setLinkSource] = useState('google_drive');
  const [linkName, setLinkName] = useState('');
  const [linkUrl, setLinkUrl] = useState('');
  const [savingLink, setSavingLink] = useState(false);
  // Block I: a failed load, add, link or remove is said, where it happened (it was swallowed, and a
  // failed load read as "No files attached to this project yet").
  const [loadError, setLoadError] = useState('');
  const [fileFailure, setFileFailure] = useState('');

  const loadFiles = useCallback(() => {
    setLoading(true);
    setLoadError('');
    return api(`/api/projects/${project.id}/files`)
      .then(r => r.json())
      .then(d => { setFiles(Array.isArray(d.files) ? d.files : []); })
      .catch((err) => setLoadError(messageFromError(err, "This project's files could not be loaded.")))
      .finally(() => setLoading(false));
  }, [project.id]);

  useEffect(() => { loadFiles(); }, [loadFiles]);

  async function addFile() {
    if (!newFileName.trim() || !newFileUrl.trim()) return;
    setSaving(true);
    setFileFailure('');
    try {
      const res = await api(`/api/projects/${project.id}/files`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ fileName: newFileName.trim(), fileUrl: newFileUrl.trim(), source: 'upload' }),
      });
      const data = await res.json();
      if (!data.file) throw new Error('The server did not confirm the file was attached. Please try again.');
      setFiles(prev => [data.file, ...prev]);
      setNewFileName('');
      setNewFileUrl('');
      setAddingFile(false);
    } catch (err) {
      setFileFailure(describeWriteFailure(err, 'attach that file'));
    } finally {
      setSaving(false);
    }
  }

  async function addLink() {
    if (!linkName.trim() || !linkUrl.trim()) return;
    setSavingLink(true);
    setFileFailure('');
    try {
      const res = await api(`/api/projects/${project.id}/files`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ fileName: linkName.trim(), externalUrl: linkUrl.trim(), source: linkSource }),
      });
      const data = await res.json();
      if (!data.file) throw new Error('The server did not confirm the link was added. Please try again.');
      setFiles(prev => [data.file, ...prev]);
      setLinkName('');
      setLinkUrl('');
      setLinkingFile(false);
    } catch (err) {
      setFileFailure(describeWriteFailure(err, 'link that file'));
    } finally {
      setSavingLink(false);
    }
  }

  async function removeFile(fileId) {
    setFileFailure('');
    try {
      await api(`/api/projects/${project.id}/files/${fileId}`, { method: 'DELETE' });
      setFiles(prev => prev.filter(f => f.id !== fileId));
    } catch (err) {
      setFileFailure(describeWriteFailure(err, 'remove that file'));
    }
  }

  if (loading) return <Skeleton lines={3} label="Loading files" />;
  if (loadError) return <ErrorState title="Files did not load" message={loadError} onRetry={loadFiles} />;

  return (
    <div className="pj-stack pj-stack--tight">
      {files.length > 0 && (
        <ul className="pj-files">
          {files.map(f => (
            <li key={f.id} className="pj-file">
              <ProjFileIcon source={f.source} mimeType={f.mimeType} />
              <span className="pj-file__text">
                <span className="pj-file__name" title={f.fileName}>{f.fileName}</span>
                <span className="pj-file__meta">
                  {f.source && f.source !== 'upload' && <StatusPill tone="neutral">{SOURCE_LABEL[f.source] || 'Link'}</StatusPill>}
                  {f.uploadedAt && <span>{formatDate(f.uploadedAt, undefined, { day: 'numeric', month: 'short', year: 'numeric' })}</span>}
                </span>
              </span>
              <a className="pj-file__link" href={f.externalUrl || f.fileUrl} target="_blank" rel="noopener noreferrer">
                {f.source && f.source !== 'upload' ? 'Open' : 'Download'}
              </a>
              <Button size="sm" variant="destructive" aria-label={`Remove ${f.fileName}`} onClick={() => removeFile(f.id)}>Remove</Button>
            </li>
          ))}
        </ul>
      )}
      {fileFailure && (
        <p role="alert" data-testid="project-file-failed" className="pj-failed">{fileFailure}</p>
      )}
      {files.length === 0 && !addingFile && !linkingFile && (
        <EmptyState title="No files yet">Attach a file or link one from Google Drive, OneDrive or the web.</EmptyState>
      )}
      {addingFile && (
        <Reveal className="pj-form">
          <Field label="File name"><Input value={newFileName} onChange={e => setNewFileName(e.target.value)} placeholder="e.g. Creative brief.pdf" /></Field>
          <Field label="File address"><Input value={newFileUrl} onChange={e => setNewFileUrl(e.target.value)} placeholder="https://…" /></Field>
          <div className="pj-row">
            <Button variant="confirm" onClick={addFile} disabled={saving || !newFileName.trim() || !newFileUrl.trim()} aria-busy={saving || undefined}>
              {saving ? 'Saving…' : 'Save'}
            </Button>
            <Button variant="tertiary" onClick={() => { setAddingFile(false); setNewFileName(''); setNewFileUrl(''); }}>Cancel</Button>
          </div>
        </Reveal>
      )}
      {linkingFile && (
        <Reveal className="pj-form">
          <Field label="Where it is">
            <Select value={linkSource} onChange={e => setLinkSource(e.target.value)}
              options={[{ value: 'google_drive', label: 'Google Drive' }, { value: 'onedrive', label: 'OneDrive' }, { value: 'url', label: 'Other link' }]} />
          </Field>
          <Field label="Name"><Input data-reveal-focus value={linkName} onChange={e => setLinkName(e.target.value)} placeholder="e.g. Brand guidelines" /></Field>
          <Field label="Link" hint="It must start with https://"><Input value={linkUrl} onChange={e => setLinkUrl(e.target.value)} placeholder="https://…" /></Field>
          <div className="pj-row">
            <Button variant="confirm" onClick={addLink} disabled={savingLink || !linkName.trim() || !linkUrl.trim()} aria-busy={savingLink || undefined}>
              {savingLink ? 'Linking…' : 'Link'}
            </Button>
            <Button variant="tertiary" onClick={() => { setLinkingFile(false); setLinkName(''); setLinkUrl(''); }}>Cancel</Button>
          </div>
        </Reveal>
      )}
      {!addingFile && !linkingFile && (
        <div className="pj-row">
          <Button variant="secondary" onClick={() => setAddingFile(true)}>+ Attach file</Button>
          <Button variant="tertiary" onClick={() => setLinkingFile(true)}>🔗 Link file</Button>
        </div>
      )}
    </div>
  );
}

// ── Notes Tab ─────────────────────────────────────────────────────────────────

function NotesTab({ project }) {
  // 2026-10-21 (Carla's items 3/4): the box is editable only once the STORED notes have loaded. It
  // rendered the list item's copy first, so typing before the load was replaced on screen by the load
  // while its own save overwrote the stored notes, and a failed load left the stale copy editable.
  const [notes, setNotes] = useState('');
  const [loaded, setLoaded] = useState(false);
  const [saved, setSaved] = useState(false);
  // Block I: the load and the autosave say when they fail. "Saved" follows only a confirmed PUT.
  const [loadFailure, setLoadFailure] = useState('');
  const [saveFailure, setSaveFailure] = useState('');
  const timerRef = useRef(null);
  const latestRef = useRef('');
  const pendingRef = useRef(null);   // the words typed but not yet sent
  const aliveRef = useRef(true);

  const loadNotes = useCallback(() => {
    setLoadFailure('');
    return api(`/api/projects/${project.id}/notes`)   // api() throws on a non-2xx
      .then(r => r.json())
      .then(d => {
        if (typeof d?.notes !== 'string') throw new Error('Aidey sent back something it could not read.');
        setNotes(d.notes); latestRef.current = d.notes; setLoaded(true);
      })
      .catch((err) => setLoadFailure(messageFromError(err, 'The saved notes could not be loaded.')));
  }, [project.id]);
  useEffect(() => { loadNotes(); }, [loadNotes]);

  const send = useCallback(async (val) => {
    pendingRef.current = null;
    try {
      const res = await api(`/api/projects/${project.id}/notes`, { method: 'PUT', body: JSON.stringify({ notes: val }) });
      const data = await res.json().catch(() => null);
      if (!data?.ok) throw new Error('The server did not confirm the notes were saved.');
      if (!aliveRef.current) return;
      setSaveFailure('');
      // Only say Saved for what is in the box now; a later keystroke has its own save coming.
      if (latestRef.current === val) {
        setSaved(true);
        setTimeout(() => { if (aliveRef.current) setSaved(false); }, 2000);
      }
    } catch (err) {
      const message = describeWriteFailure(err, 'save these notes');
      if (aliveRef.current) setSaveFailure(message);
      else showErrorToast(`Couldn't save the project notes. ${message}`);
    }
  }, [project.id]);

  // Leaving the tab inside the pause SENDS what was typed; it used to be dropped silently.
  useEffect(() => () => {
    aliveRef.current = false;
    clearTimeout(timerRef.current);
    if (pendingRef.current !== null) send(pendingRef.current);
  }, [send]);

  const handleChange = (e) => {
    const val = e.target.value;
    setNotes(val);
    latestRef.current = val;
    pendingRef.current = val;
    setSaved(false);
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => send(val), 800);
  };

  return (
    <div className="pj-stack pj-stack--tight">
      {loadFailure && !loaded && <ErrorState title="Notes did not load" message={`${loadFailure} Nothing has been changed.`} onRetry={loadNotes} />}
      {!loaded && !loadFailure && <p className="pj-muted" role="status">Loading the notes…</p>}
      {loaded && (
        <Field label="Project notes" hint="Saved as you type, and the same on every device.">
          <Textarea className="pj-notes" rows={14} value={notes} onChange={handleChange}
            placeholder={`Add notes for ${project.name}…\n\nMeeting notes, decisions, ideas, anything relevant to this project.`} />
        </Field>
      )}
      {saved && !saveFailure && <span className="pj-saved" role="status">✓ Saved</span>}
      {saveFailure && (
        <p role="alert" data-testid="project-notes-failed" className="pj-failed">Couldn't save, your text is kept here. {saveFailure}</p>
      )}
      {/* Decision 90 v2: the notes in Notes linked to this project, each opening in the Notes editor. */}
      <LinkedNotes kind="project" id={project.id} title="Notes linked to this project"
        emptyText="No notes are linked to this project yet." />
      {/* Email Phase 1 (2026-10-21): the emails with its client, from her own mailboxes. */}
      <ProjectEmails projectId={project.id} />
    </div>
  );
}
